import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Loader2, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getPublicHolidayCountries } from '@/services/api/timesheetSettingsApi';

interface CountrySelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentCountryCode?: string | null;
  employeeName?: string;
  onSelect: (countryCode: string) => Promise<boolean>;
}

export const CountrySelectionModal: React.FC<CountrySelectionModalProps> = ({
  open,
  onOpenChange,
  currentCountryCode,
  employeeName,
  onSelect,
}) => {
  const [countries, setCountries] = useState<Array<{ countryCode: string; name: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (open) {
      loadCountries();
      setSelectedCountryCode(currentCountryCode || null);
      setSearchQuery('');
    }
  }, [open, currentCountryCode]);

  const loadCountries = async () => {
    setIsLoading(true);
    try {
      const data = await getPublicHolidayCountries();
      setCountries(data ?? []);
    } catch (err) {
      console.error('Error loading countries:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedCountryCode) return;

    setIsSubmitting(true);
    try {
      const success = await onSelect(selectedCountryCode);
      if (success) {
        onOpenChange(false);
      }
    } catch (err) {
      console.error('Error assigning country:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredCountries = countries.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.countryCode.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Assign Country</DialogTitle>
          <DialogDescription>
            Select the public holiday country for{' '}
            {employeeName ? `${employeeName}'s` : "this employee's"} timesheets
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : (
            <div className="flex flex-col min-h-0 space-y-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search countries..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Country List */}
              <div className="overflow-y-auto max-h-[400px] pr-1">
                <RadioGroup
                  value={selectedCountryCode || ''}
                  onValueChange={setSelectedCountryCode}
                  className="space-y-2"
                >
                  {filteredCountries.map((country) => {
                    const isSelected = selectedCountryCode === country.countryCode;

                    return (
                      <div
                        key={country.countryCode}
                        className={cn(
                          'border rounded-lg p-3 cursor-pointer transition-all',
                          isSelected
                            ? 'bg-primary/5 border-primary border-2'
                            : 'border-border hover:border-muted-foreground/30'
                        )}
                        onClick={() => setSelectedCountryCode(country.countryCode)}
                      >
                        <div className="flex items-center gap-3">
                          <RadioGroupItem
                            value={country.countryCode}
                            id={country.countryCode}
                          />
                          <Label
                            htmlFor={country.countryCode}
                            className="flex-1 cursor-pointer font-medium"
                          >
                            {country.name}
                          </Label>
                          <span className="text-xs text-muted-foreground">
                            {country.countryCode}
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  {filteredCountries.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No countries found matching &ldquo;{searchQuery}&rdquo;
                    </p>
                  )}
                </RadioGroup>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedCountryCode || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              'Assign Country'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
